import { type JSX, useEffect, useRef } from "react";
import SelectComponent from "@via/components/select/select";
import OperatorSelect from "@via/components/advanced-filter/operator-select";
import ValueInput from "@via/components/advanced-filter/value-input";
import { SelectOption } from "@via/utils/interfaces";
import { FieldTypeEnum } from "@via/components/advanced-filter/helpers/types";
import { useForm, useWatch } from "react-hook-form";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { type Options } from "react-select";

interface FileQueryFilterConditionProps {
  index: number;
  field: string;
  operator: string;
  value: any;
  onFieldChange: (index: number, field: string) => void;
  onOperatorChange: (index: number, operator: string) => void;
  onValueChange: (index: number, value: any) => void;
  onRemove: (index: number) => void;
  fieldOptions: SelectOption[];
  showLogicSelector?: boolean;
  logic?: "AND" | "OR";
}

export default function FileQueryFilterCondition({
  index,
  field,
  operator,
  value,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onRemove,
  fieldOptions,
  showLogicSelector = false,
  logic = "AND",
}: FileQueryFilterConditionProps): JSX.Element {
  // Create a local form for this condition
  const { control, setValue } = useForm({
    defaultValues: {
      field: field || "",
      operator: operator || "",
      value: value || "",
    },
  });

  // Watch form field value to determine if inputs should be enabled
  const watchedField = useWatch({
    control,
    name: "field",
  });

  // Use form field value if available, otherwise fall back to prop
  const currentField = watchedField || field || "";

  // Determine field type - report query filter fields are always numbers
  const fieldType = FieldTypeEnum.Number;

  const watchedOperator = useWatch({
    control,
    name: "operator",
  });

  const watchedValue = useWatch({
    control,
    name: "value",
  });

  const previousValueRef = useRef<any>(value);
  const previousOperatorRef = useRef<string>(operator);
  const previousFieldRef = useRef<string>(field);
  const isInitialMountRef = useRef(true);
  const isUserInputRef = useRef(false);

  // Initialize form values on mount
  useEffect(() => {
    setValue("field", field || "");
    setValue("operator", operator || "");
    setValue("value", value || "");
    previousValueRef.current = value;
    previousOperatorRef.current = operator;
    previousFieldRef.current = field;
    isInitialMountRef.current = false;
  }, []);

  // Sync form values with props when they change externally (e.g., when editing existing filter)
  useEffect(() => {
    if (isInitialMountRef.current || isUserInputRef.current) {
      isUserInputRef.current = false;
      return;
    }

    // Only sync if props changed externally (different from refs)
    if (field !== previousFieldRef.current) {
      setValue("field", field || "");
      previousFieldRef.current = field;
    }
    if (operator !== previousOperatorRef.current) {
      setValue("operator", operator || "");
      previousOperatorRef.current = operator;
    }
    if (value !== previousValueRef.current) {
      setValue("value", value || "");
      previousValueRef.current = value;
    }
  }, [field, operator, value, setValue]);

  // Handle operator changes from form
  useEffect(() => {
    if (isInitialMountRef.current) return;

    if (
      watchedOperator !== undefined &&
      watchedOperator !== previousOperatorRef.current
    ) {
      isUserInputRef.current = true;
      previousOperatorRef.current = watchedOperator;
      onOperatorChange(index, watchedOperator);
    }
  }, [watchedOperator, index, onOperatorChange]);

  // Handle value changes from form
  useEffect(() => {
    if (isInitialMountRef.current) return;

    if (watchedValue !== undefined) {
      const prevVal = previousValueRef.current;
      const newVal = watchedValue;

      // Check if values are actually different
      if (
        prevVal !== newVal &&
        String(prevVal || "") !== String(newVal || "")
      ) {
        isUserInputRef.current = true;
        previousValueRef.current = newVal;
        onValueChange(index, newVal);
      }
    }
  }, [watchedValue, index, onValueChange]);

  // Handle field changes from form - sync to parent state
  useEffect(() => {
    if (isInitialMountRef.current) return;

    if (watchedField !== undefined) {
      const prevField = previousFieldRef.current;
      const newField = String(watchedField || "");

      // Check if field values are actually different
      if (prevField !== newField && String(prevField || "") !== newField) {
        // Only update parent if field is not empty
        if (newField.trim() !== "") {
          isUserInputRef.current = true;
          previousFieldRef.current = newField;
          onFieldChange(index, newField);
        }
      }
    }
  }, [watchedField, index, onFieldChange]);

  // Handle field change
  const handleFieldChange = (
    selected: SelectOption | Options<SelectOption>
  ): void => {
    if (selected && !Array.isArray(selected)) {
      const fieldValue = (selected as SelectOption).value;

      // Validate fieldValue is truthy before proceeding
      if (!fieldValue) {
        return;
      }

      const fieldValueStr = String(fieldValue).trim();

      // Only proceed if field value is not empty
      if (fieldValueStr === "") {
        return;
      }

      isUserInputRef.current = true;
      setValue("field", fieldValueStr);
      onFieldChange(index, fieldValueStr);
      // Reset operator and value when field changes
      setValue("operator", "");
      setValue("value", "");
      onOperatorChange(index, "");
      onValueChange(index, "");
    }
  };

  const selectedFieldOption = fieldOptions.find(opt => opt.value === field);

  return (
    <div className="flex items-center gap-2">
      {showLogicSelector && index > 0 && (
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-500">{logic}</span>
        </div>
      )}
      <div className="flex-1 grid grid-cols-3 gap-2 items-center">
        <div>
          <SelectComponent
            name="fieldChangeComponent"
            value={selectedFieldOption || null}
            onChange={handleFieldChange}
            options={fieldOptions}
            placeholder="Select field"
            enableClear={false}
            menuPosition="fixed"
            menuShouldScrollIntoView={false}
          />
        </div>
        <div>
          <OperatorSelect
            control={control}
            name="operator"
            fieldType={fieldType}
            placeholder="Select operator"
            enableClear={false}
            disabled={!currentField || currentField.trim() === ""}
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 -mt-2">
            <ValueInput
              control={control}
              name="value"
              fieldType={fieldType}
              placeholder="Enter value"
              disabled={!currentField || currentField.trim() === ""}
              label=""
            />
          </div>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="rounded p-1 hover:bg-gray-200 flex-shrink-0">
            <XMarkIcon className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>
    </div>
  );
}
